// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { AVALANCHE_SERVICE_CONFIGURATION_ADDR } from './DeploymentConfigsProd'
import { ChainConfiguration, StageConfiguration, UserConnection } from './DeploymentConfigTypes'
import { ChainId, ETHERSCAN_AVALANCHE, RoleEnum, WalletEnum } from './Enums'

const LOCAL_HH_URL = 'http://127.0.0.1:8545'
const LOCAL_HH_WS_URL = 'ws://127.0.0.1:8545'

const BLOCK_EXPLORER_HARDHAT = 'https://sepolia.etherscan.io/'

export const LYT_POOL_ADDR = '0x29203dD00f5a2627E0483D0d18930e55f4892746'

// PROVIDERS

let REACT_APP_ACCOUNT_OPERATOR: string | undefined = undefined
let REACT_APP_ACCOUNT_POOL_ADMIN: string | undefined = undefined
let REACT_APP_ACCOUNT_LENDER1: string | undefined = undefined
let REACT_APP_ACCOUNT_BORROWER: string | undefined = undefined
let REACT_APP_ACCOUNT_LENDER2: string | undefined = undefined
let REACT_APP_ACCOUNT_LENDER3: string | undefined = undefined
let REACT_APP_OPERATOR: string | undefined = undefined
let REACT_APP_POOL_ADMIN: string | undefined = undefined
let REACT_APP_BORROWER: string | undefined = undefined
let REACT_APP_LENDER1: string | undefined = undefined
let REACT_APP_LENDER2: string | undefined = undefined
let REACT_APP_LENDER3: string | undefined = undefined
let REACT_APP_ACCOUNT_ISSUER: string | undefined = undefined
let REACT_APP_ISSUER: string | undefined = undefined
let REACT_APP_ACCOUNT_ISSUER_VAULT: string | undefined = undefined
let REACT_APP_ISSUER_VAULT: string | undefined = undefined
let REACT_APP_ACCOUNT_MARKET_MAKER: string | undefined = undefined
let REACT_APP_MARKET_MAKER: string | undefined = undefined
let REACT_APP_ACCOUNT_MARKET_MAKER2: string | undefined = undefined
let REACT_APP_MARKET_MAKER2: string | undefined = undefined

try {
  REACT_APP_ACCOUNT_OPERATOR = process.env.REACT_APP_ACCOUNT_OPERATOR
  REACT_APP_ACCOUNT_POOL_ADMIN = process.env.REACT_APP_ACCOUNT_POOL_ADMIN
  REACT_APP_ACCOUNT_BORROWER = process.env.REACT_APP_ACCOUNT_BORROWER
  REACT_APP_ACCOUNT_LENDER1 = process.env.REACT_APP_ACCOUNT_LENDER1
  REACT_APP_ACCOUNT_LENDER2 = process.env.REACT_APP_ACCOUNT_LENDER2 as string
  REACT_APP_ACCOUNT_LENDER3 = process.env.REACT_APP_ACCOUNT_LENDER3 as string
  REACT_APP_POOL_ADMIN = process.env.REACT_APP_POOL_ADMIN as string
  REACT_APP_BORROWER = process.env.REACT_APP_BORROWER as string
  REACT_APP_LENDER1 = process.env.REACT_APP_LENDER1 as string
  REACT_APP_LENDER2 = process.env.REACT_APP_LENDER2 as string
  REACT_APP_LENDER3 = process.env.REACT_APP_LENDER3 as string
  REACT_APP_OPERATOR = process.env.REACT_APP_OPERATOR as string
  REACT_APP_ACCOUNT_ISSUER = process.env.REACT_APP_ACCOUNT_ISSUER as string
  REACT_APP_ISSUER = process.env.REACT_APP_ISSUER as string
  REACT_APP_ACCOUNT_ISSUER_VAULT = process.env.REACT_APP_ACCOUNT_ISSUER_VAULT as string
  REACT_APP_ISSUER_VAULT = process.env.REACT_APP_ISSUER_VAULT as string
  REACT_APP_ACCOUNT_MARKET_MAKER = process.env.REACT_APP_ACCOUNT_MARKET_MAKER as string
  REACT_APP_MARKET_MAKER = process.env.REACT_APP_MARKET_MAKER as string
  REACT_APP_ACCOUNT_MARKET_MAKER2 = process.env.REACT_APP_ACCOUNT_MARKET_MAKER2 as string
  REACT_APP_MARKET_MAKER2 = process.env.REACT_APP_MARKET_MAKER2 as string
} catch {
  REACT_APP_ACCOUNT_OPERATOR = undefined
  REACT_APP_ACCOUNT_POOL_ADMIN = undefined
  REACT_APP_ACCOUNT_LENDER1 = undefined
  REACT_APP_ACCOUNT_BORROWER = undefined
  REACT_APP_ACCOUNT_LENDER2 = undefined
  REACT_APP_ACCOUNT_LENDER3 = undefined
  REACT_APP_POOL_ADMIN = undefined
  REACT_APP_BORROWER = undefined
  REACT_APP_LENDER1 = undefined
  REACT_APP_LENDER2 = undefined
  REACT_APP_LENDER3 = undefined
  REACT_APP_OPERATOR = undefined
  REACT_APP_ACCOUNT_ISSUER = undefined
  REACT_APP_ISSUER = undefined
  REACT_APP_ACCOUNT_ISSUER_VAULT = undefined
  REACT_APP_ISSUER_VAULT = undefined
  REACT_APP_ACCOUNT_MARKET_MAKER = undefined
  REACT_APP_MARKET_MAKER = undefined
  REACT_APP_ACCOUNT_MARKET_MAKER2 = undefined
  REACT_APP_MARKET_MAKER2 = undefined
  // noop
}

export const LOCALHOST_SERVICE_CONFIGURATION_ADDR = '0x29AD3b3231a7a1a2f5Bc44f2C3dA27F50Cb1cB97'

const LOCAL_HOST = process.env.GITHUB_RUN_NUMBER ? 'localdev' : 'localdev'

const CHAIN_CONFIG_LOCALHOST_LYT: ChainConfiguration = {
  chainConfigurationName: 'LytHardhat',
  chainId: ChainId.HARDHAT,
  chainName: 'LocalHardhat',
  etherscanUrl: 'https://sepolia.etherscan.io',
  blockExplorerUrl: BLOCK_EXPLORER_HARDHAT,
  serviceConfigurationAddr: '0x04461d1Eb6A44B22ACC697197a1b030f3946a961',
  frontendProviderUrl: LOCAL_HH_URL,
  frontendWebSocketProviderUrl: LOCAL_HH_WS_URL,
  backendProviderUrl: LOCAL_HH_URL,
  walletConnectProjectId: undefined,
  chainIcon: 'hardhat',

  nativeCurrency: {
    decimals: 18,
    name: 'Ether',
    symbol: 'ETH',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 751532,
    },
  },
  isTestnet: true,
}

export const CHAIN_CONFIG_LOCALHOST: ChainConfiguration = {
  chainConfigurationName: 'Hardhat',
  chainId: ChainId.HARDHAT,
  chainName: 'LocalHardhat',
  etherscanUrl: 'https://sepolia.etherscan.io',
  blockExplorerUrl: BLOCK_EXPLORER_HARDHAT,
  serviceConfigurationAddr: LOCALHOST_SERVICE_CONFIGURATION_ADDR,

  frontendProviderUrl: LOCAL_HH_URL,
  frontendWebSocketProviderUrl: LOCAL_HH_WS_URL,
  backendProviderUrl: LOCAL_HH_URL,
  walletConnectProjectId: undefined,
  chainIcon: 'hardhat',

  nativeCurrency: {
    decimals: 18,
    name: 'Ether',
    symbol: 'ETH',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 751532,
    },
  },
  isTestnet: true,
  hiddenInvestVaultAddresses: ['0x8522f7dF9304ADFA4D6671f760CaB27900C566f4'],
}

const USER_CONNECTION_OPERATOR: UserConnection = {
  roleEnum: RoleEnum.OPERATOR,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_OPERATOR as string,
  accountAddr: REACT_APP_OPERATOR as string,
  companyProfile: {
    isIssuer: false,
    uuid: '',
    companyName: 'OpenTrade test Operator',
    wallets: [REACT_APP_POOL_ADMIN as string],
    emails: ['tom+testOperator@open-trade.io'],
  },
  signer: undefined,
}
const USER_CONNECTION_POOLADMIN: UserConnection = {
  roleEnum: RoleEnum.POOL_ADMIN,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_POOL_ADMIN as string,
  accountAddr: REACT_APP_POOL_ADMIN as string,
  companyProfile: {
    uuid: '',
    isIssuer: false,
    companyName: 'OpenTrade test Pool Admin',
    wallets: [REACT_APP_POOL_ADMIN as string],
    emails: ['tom+testPooladmin@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_MN_POOL_ADMIN: UserConnection = {
  roleEnum: RoleEnum.POOL_ADMIN,
  walletEnum: WalletEnum.IMPERSONATE_ACCOUNT,

  accountAddr: '0xbd0Ca95C5A104E14E60d0868667AF174D146C9FD',
  companyProfile: {
    uuid: '',
    isIssuer: false,
    companyName: 'Mainnet Pool Admin(Impersonate)',
    wallets: ['0xbd0Ca95C5A104E14E60d0868667AF174D146C9FD'],
    emails: ['tom+testPooladmin@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_BORROWER: UserConnection = {
  roleEnum: RoleEnum.BORROWER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_BORROWER as string,
  accountAddr: REACT_APP_BORROWER as string,
  companyProfile: {
    uuid: '',
    companyName: 'OpenTrade test Borrower',
    wallets: [REACT_APP_BORROWER as string],
    emails: ['tom+testBorrower@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_LENDER1: UserConnection = {
  roleEnum: RoleEnum.LENDER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_LENDER1 as string,
  accountAddr: REACT_APP_LENDER1 as string,
  companyProfile: {
    uuid: '',
    companyName: 'OpenTrade test Lender1',
    wallets: [REACT_APP_LENDER1 as string],
    emails: ['tom+testLender1@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_LENDER2: UserConnection = {
  roleEnum: RoleEnum.LENDER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_LENDER2 as string,
  accountAddr: REACT_APP_LENDER2 as string,
  companyProfile: {
    uuid: '',
    companyName: 'OpenTrade test Lender2',
    wallets: [REACT_APP_LENDER2 as string],
    emails: ['tom+testLender2@open-trade.io'],
  },
  signer: undefined,
}
const USER_CONNECTION_LENDER3: UserConnection = {
  roleEnum: RoleEnum.LENDER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_LENDER3 as string,
  accountAddr: REACT_APP_LENDER3 as string,
  companyProfile: {
    uuid: '',
    companyName: 'OpenTrade test Lender3',
    wallets: [REACT_APP_LENDER3 as string],
    emails: ['tom+testLender3@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_PROTOCOL_MANAGER: UserConnection = {
  roleEnum: RoleEnum.PROTOCOL_MANAGER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_POOL_ADMIN as string,
  accountAddr: REACT_APP_POOL_ADMIN as string,
  companyProfile: {
    uuid: '',
    companyName: 'Protocol Manager',
    wallets: [REACT_APP_POOL_ADMIN as string],
    emails: ['tom+testPooladmin@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_ISSUER: UserConnection = {
  roleEnum: RoleEnum.ISSUER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_ISSUER as string,
  accountAddr: REACT_APP_ISSUER as string,
  companyProfile: {
    uuid: '',
    isIssuer: true,
    companyName: 'Issuer',
    wallets: [REACT_APP_ISSUER as string],
    emails: ['tom+testPooladmin@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_ISSUER_VAULT: UserConnection = {
  roleEnum: RoleEnum.ISSUER_VAULT_WALLET,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_ISSUER_VAULT as string,
  accountAddr: REACT_APP_ISSUER_VAULT as string,
  companyProfile: {
    uuid: '',
    companyName: 'Issuer Vault',
    wallets: [REACT_APP_ISSUER_VAULT as string],
    emails: ['tom+testPooladmin@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_MARKET_MAKER: UserConnection = {
  roleEnum: RoleEnum.MARKET_MAKER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_MARKET_MAKER as string,
  accountAddr: REACT_APP_MARKET_MAKER as string,
  companyProfile: {
    uuid: '',
    companyName: 'Market Maker1',
    wallets: [REACT_APP_MARKET_MAKER as string],
    emails: ['tom+testPooladmin@open-trade.io'],
  },
  signer: undefined,
}

const USER_CONNECTION_MARKET_MAKER2: UserConnection = {
  roleEnum: RoleEnum.MARKET_MAKER,
  walletEnum: WalletEnum.PRIVATE_KEY_STRING,
  signerKey: REACT_APP_ACCOUNT_MARKET_MAKER2 as string,
  accountAddr: REACT_APP_MARKET_MAKER2 as string,
  companyProfile: {
    uuid: '',
    companyName: 'Market Maker2',
    wallets: [REACT_APP_MARKET_MAKER2 as string],
    emails: ['tom+testPooladmin@open-trade.io'],
  },
  signer: undefined,
}
export const LOCAL_COGNITO_CONFIG_CLIENT_ID = 'LOCAL_COGNITO_CONFIG_CLIENT_ID'
export const LOCAL_COGNITO_CONFIG_USER_POOL_ID = 'LOCAL_COGNITO_CONFIG_USER_POOL_ID'
export const LOCAL_STAGE_CONFIG: StageConfiguration = {
  stageName: 'local', // from serverless.yml
  domain: 'localhost',
  serverApiBaseUrl: `http://${LOCAL_HOST}:7000`,
  websocketUrl: `http://${LOCAL_HOST}:3001`,
  graphqlUrl: `http://${LOCAL_HOST}:7000`,
  restAPIKey: 'TODO',
  chainConfigurations: [CHAIN_CONFIG_LOCALHOST],
  supportedChains: [],
  userConnections: [
    USER_CONNECTION_POOLADMIN,
    USER_CONNECTION_BORROWER,
    USER_CONNECTION_LENDER1,
    USER_CONNECTION_LENDER2,
    USER_CONNECTION_OPERATOR,
    USER_CONNECTION_ISSUER_VAULT,
  ],
  isLyt: false,
  cognitoConfig: {
    clientId: LOCAL_COGNITO_CONFIG_CLIENT_ID,
    userPoolId: LOCAL_COGNITO_CONFIG_USER_POOL_ID,
    region: 'us-east-1',
  },
}
const CHAIN_CONFIG_LOCALHOST_PROD_CANARY: ChainConfiguration = {
  chainConfigurationName: 'HardhatProdCanary',
  chainId: ChainId.AVALANCHE,
  chainName: 'LocalHardhatProdCanary',
  etherscanUrl: ETHERSCAN_AVALANCHE,
  blockExplorerUrl: BLOCK_EXPLORER_HARDHAT,
  serviceConfigurationAddr: AVALANCHE_SERVICE_CONFIGURATION_ADDR,

  frontendProviderUrl: LOCAL_HH_URL,
  frontendWebSocketProviderUrl: LOCAL_HH_WS_URL,
  backendProviderUrl: LOCAL_HH_URL,
  walletConnectProjectId: undefined,
  chainIcon: 'hardhat',

  nativeCurrency: {
    decimals: 18,
    name: 'Ether',
    symbol: 'ETH',
  },
  contracts: {
    multicall3: {
      address: `0xca11bde05977b3631167028862be2a173976ca11` as `0x${string}`,
      blockCreated: 751532,
    },
  },
  isTestnet: true,
  hiddenInvestVaultAddresses: ['0x8522f7dF9304ADFA4D6671f760CaB27900C566f4'],
}

export const LOCAL_STAGE_CONFIG_PROD_CANARY: StageConfiguration = {
  stageName: 'localprodcanary', // from serverless.yml
  domain: 'localhost',
  serverApiBaseUrl: `http://${LOCAL_HOST}:7000`,
  websocketUrl: `http://${LOCAL_HOST}:3001`,
  graphqlUrl: `http://${LOCAL_HOST}:7000`,
  restAPIKey: 'TODO',
  chainConfigurations: [CHAIN_CONFIG_LOCALHOST_PROD_CANARY],
  supportedChains: [],
  userConnections: [
    USER_CONNECTION_POOLADMIN,
    USER_CONNECTION_BORROWER,
    USER_CONNECTION_LENDER1,
    USER_CONNECTION_LENDER2,
    USER_CONNECTION_OPERATOR,
    USER_CONNECTION_ISSUER_VAULT,

    USER_CONNECTION_MN_POOL_ADMIN,
  ],
  isLyt: false,
  cognitoConfig: {
    clientId: LOCAL_COGNITO_CONFIG_CLIENT_ID,
    userPoolId: LOCAL_COGNITO_CONFIG_USER_POOL_ID,
    region: 'us-east-1',
  },
}
export const LOCAL_LYT_STAGE_CONFIG: StageConfiguration = {
  stageName: 'localsierra', // from serverless.yml
  domain: 'localhost',
  serverApiBaseUrl: `http://${LOCAL_HOST}:7100`,
  websocketUrl: `http://${LOCAL_HOST}:3101`,
  graphqlUrl: '',
  lytPoolAddr: LYT_POOL_ADDR,
  restAPIKey: 'TODO',
  chainConfigurations: [CHAIN_CONFIG_LOCALHOST_LYT],
  supportedChains: [],
  userConnections: [
    USER_CONNECTION_PROTOCOL_MANAGER,
    USER_CONNECTION_ISSUER,
    USER_CONNECTION_ISSUER_VAULT,
    USER_CONNECTION_MARKET_MAKER,

    USER_CONNECTION_MARKET_MAKER2,
    USER_CONNECTION_BORROWER,
    USER_CONNECTION_LENDER1,
    USER_CONNECTION_LENDER2,
    USER_CONNECTION_LENDER3,
  ],
  parentStageConfiguration: LOCAL_STAGE_CONFIG,
  parentChainConfiguration: CHAIN_CONFIG_LOCALHOST,

  isLyt: true,

  oftAdapter: '0x8B9DCCf3B8d5F1dC9F2Ab9f34cA8dc674FfDe6Ae',
  layerZeroEid: 40106, // AVALANCHE_V2_TESTNET (Fuji)
}
